import { hasValue } from '@contexts/shared/domain';
import {
	type StorageRepository,
	Store,
	type StoreKey
} from '@contexts/shared/domain/contracts/storage.repository';
import { isValidKeyForStore } from '@contexts/shared/domain/utils/validators/is-valid-store-key';

export function indexedDBStorageRepositoryBuilder(): StorageRepository {
	const dbName = 'storage-webapp';

	async function getStore(store: Store): Promise<IDBDatabase> {
		return new Promise((resolve, reject) => {
			let request = indexedDB.open(dbName);

			request.onupgradeneeded = () => {
				const db = request.result;

				for (const storeName of Object.values(Store)) {
					if (!db.objectStoreNames.contains(storeName)) {
						db.createObjectStore(storeName);
					}
				}
			};

			request.onsuccess = () => {
				const db = request.result;

				if (!db.objectStoreNames.contains(store)) {
					db.close();

					request = indexedDB.open(dbName, db.version + 1);
					request.onupgradeneeded = () => {
						const newDb = request.result;
						newDb.createObjectStore(store);
					};
					request.onsuccess = () => resolve(request.result);
					request.onerror = () => reject(request.error);
				} else {
					resolve(db);
				}
			};

			request.onerror = () => reject(request.error);
		});
	}

	async function waitForTransaction(tx: IDBTransaction): Promise<void> {
		return new Promise((resolve, reject) => {
			tx.oncomplete = () => resolve();
			tx.onerror = () => reject(tx.error);
			tx.onabort = () => reject(new Error('Transaction aborted'));
		});
	}

	return {
		async setItem<T extends Store, K extends StoreKey<T>>(
			store: T,
			key: K,
			value: unknown
		): Promise<void> {
			if (!isValidKeyForStore(store, key)) {
				throw new Error(`Invalid key "${key}" for store "${store}"`);
			}
			const db = await getStore(store);
			const tx = db.transaction(store, 'readwrite');
			const storeObject = tx.objectStore(store);
			storeObject.put(value, key);
			await waitForTransaction(tx);
		},

		async getItem<T extends Store, K extends StoreKey<T>>(store: T, key: K): Promise<unknown> {
			if (!isValidKeyForStore(store, key)) {
				throw new Error(`Invalid key "${key}" for store "${store}"`);
			}
			const db = await getStore(store);
			return new Promise((resolve, reject) => {
				const tx = db.transaction(store, 'readonly');
				const storeObject = tx.objectStore(store);
				const request = storeObject.get(key);

				request.onsuccess = () => {
					if (request.result === undefined) {
						resolve(null);
					} else {
						resolve(request.result);
					}
				};
				request.onerror = () => reject(request.error);
			});
		},

		async hasItem<T extends Store, K extends StoreKey<T>>(store: T, key: K): Promise<boolean> {
			try {
				const item = await this.getItem(store, key);
				return hasValue(item);
			} catch {
				return false;
			}
		},

		async clearKey<T extends Store, K extends StoreKey<T>>(store: T, key: K): Promise<void> {
			if (!isValidKeyForStore(store, key)) {
				throw new Error(`Invalid key "${key}" for store "${store}"`);
			}
			const db = await getStore(store);
			const tx = db.transaction(store, 'readwrite');
			const storeObject = tx.objectStore(store);
			storeObject.delete(key);
			await waitForTransaction(tx);
		},

		async clearStore(store: Store): Promise<void> {
			const db = await getStore(store);
			const tx = db.transaction(store, 'readwrite');
			const storeObject = tx.objectStore(store);
			storeObject.clear();
			await waitForTransaction(tx);
		},

		async clearAll(): Promise<void> {
			await Promise.all(Object.values(Store).map((store) => this.clearStore(store)));
		}
	};
}
